import React, { useState } from 'react'
import { Box, Divider, Grid, Tab, Tabs } from '@material-ui/core'
import TabPanel from 'components/material/TabPanel'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Translate } from 'react-localize-redux'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { track } from 'utils/segmentTracking'
import MatchTaskGroupsWidget from 'containers/TaskManagement/MatchTaskGroupsWidget'
import useTeamFeature from 'hooks/useTeamFeature'
import TeaserChip from 'components/Teaser/TeaserChip'
import UpgradeMembershipOverlay from 'components/UpgradeMembership/UpgradeMembershipOverlay'
import PropTypes from 'prop-types'
import MatchHighlights from './MatchHighlights'
import MatchAllNotes from './MatchNotes'
import ConnectedSequences from './MatchVideoSequence/ConnectedSequences'
import LineUpContainer from './MatchLineUp'
import RatePlayers from './RatePlayers/RatePlayers'
import { MATCH_SECTIONS } from 'constants/match'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const TabViewContainer = ({ currentMatchUuid }) => {
  const [activeTab, setActiveTab] = useState(MATCH_SECTIONS.matchLineup)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { enabled: hasVideoSectionEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.video_manager_enabled
  )
  const { enabled: hasAdvancedMatchFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_match_features_enable
  )
  const { enabled: hasTaskManagerEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.task_manager_enabled
  )

  const handleChange = (event, newValue) => {
    setActiveTab(newValue)

    // Segment tracking
    switch (newValue) {
      case MATCH_SECTIONS.highlights:
        track('WClick_MP_Highlights', {
          category: 'Matchday planner',
        })
        break
      case MATCH_SECTIONS.videoSequences:
        track('WClick_MP_VideoSequence', {
          category: 'Matchday planner',
        })
        break
      case MATCH_SECTIONS.notes:
        track('WClick_MP_Notes', {
          category: 'Matchday planner',
        })
        break
      case MATCH_SECTIONS.ratePlayers:
        track('WClick_MP_RatePlayer', {
          category: 'Matchday planner',
        })
        break
      case MATCH_SECTIONS.tasks:
        track('WClick_MP_Tasks', {
          category: 'Matchday planner',
        })
        break
      default:
        break
    }
  }

  return (
    <Box m={2}>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : 'off'}
        indicatorColor="primary"
      >
        <Tab
          label={
            <Grid alignItems="center" container justifyContent="center">
              <Grid item>
                <Translate id="matches.team-line-up" />
              </Grid>
              {!hasAdvancedMatchFeaturesEnabled && <TeaserChip />}
            </Grid>
          }
          value={MATCH_SECTIONS.matchLineup}
        />
        <Tab
          label={
            <Grid alignItems="center" container justifyContent="center">
              <Translate id="matches.highlights.title" />
              {!hasAdvancedMatchFeaturesEnabled && <TeaserChip />}
            </Grid>
          }
          value={MATCH_SECTIONS.highlights}
        />
        <Tab
          label={
            <Grid alignItems="center" container justifyContent="center">
              <Translate id="matches.match-notes" />
              {!hasAdvancedMatchFeaturesEnabled && <TeaserChip />}
            </Grid>
          }
          value={MATCH_SECTIONS.notes}
        />
        <Tab
          label={
            <Grid alignItems="center" container justifyContent="center">
              <Translate id="matches.connected-sequences" />
              {!hasVideoSectionEnabled && <TeaserChip />}
            </Grid>
          }
          value={MATCH_SECTIONS.videoSequences}
        />

        <Tab
          label={
            <Grid alignItems="center" container justifyContent="center">
              <Translate id="matches.rate-players.button" />
              {!hasAdvancedMatchFeaturesEnabled && <TeaserChip />}
            </Grid>
          }
          value={MATCH_SECTIONS.ratePlayers}
        />
        <Tab
          label={
            <Grid alignItems="center" container justifyContent="center">
              <Translate id="menu.task" />
              {!hasTaskManagerEnabled && <TeaserChip />}
            </Grid>
          }
          value={MATCH_SECTIONS.tasks}
        />
      </Tabs>
      <Divider />
      <TabPanel
        value={activeTab}
        index={MATCH_SECTIONS.matchLineup}
        padding={1}
      >
        <LineUpContainer />
      </TabPanel>
      <TabPanel value={activeTab} index={MATCH_SECTIONS.highlights}>
        <MatchHighlights />
      </TabPanel>
      <TabPanel value={activeTab} index={MATCH_SECTIONS.notes}>
        <MatchAllNotes />
      </TabPanel>
      <TabPanel value={activeTab} index={MATCH_SECTIONS.videoSequences}>
        <ConnectedSequences />
      </TabPanel>
      <TabPanel
        value={activeTab}
        index={MATCH_SECTIONS.ratePlayers}
        style={{ position: 'relative' }} // To position the overlay correctly if needed
      >
        {!hasAdvancedMatchFeaturesEnabled && <UpgradeMembershipOverlay />}
        <RatePlayers />
      </TabPanel>
      <TabPanel value={activeTab} index={MATCH_SECTIONS.tasks}>
        <MatchTaskGroupsWidget eventId={currentMatchUuid} />
      </TabPanel>
    </Box>
  )
}

TabViewContainer.propTypes = {
  currentMatchUuid: PropTypes.string.isRequired,
}

export default compose(
  connect(({ matches: { current } }) => ({
    currentMatchUuid: current.uuid,
  }))
)(TabViewContainer)
